import { sortBy, groupBy, mapValues, compact, flatten, range, sum, values, pick, padEnd, sumBy, every, some, find, orderBy, map, intersection, union } from 'lodash-es';

export function getAllDivisions({ matchType }) {
  if (matchType === 'ipsc-shotgun') {
    return ['sg-open', 'sg-modified', 'sg-standard', 'sg-standard-manual'];
  }
  if (matchType === 'il-army') {
    return ['il-army-pistol', 'il-army-rifle', 'il-army-negev', 'il-army-army-rifle'];
  }
  return ['standard', 'production', 'open', 'production-optics', 'optics', 'classic', 'revolver', 'pcc-optics', 'pcc-iron'];
}

export function getAllCategories({ matchType }) {
  if (matchType === 'il-army') {
    return ['men', 'women', 'reserves'];
  }
  return ['regular', 'lady', 'super-junior', 'junior', 'grand-junior', 'lady-junior', 'lady-super-junior', 'lady-grand-junior', 'senior', 'super-senior', 'lady-senior', 'grand-senior'];
}

export function getLatestScoreForShooterPerStage({ scores, numStages, stages }) {
  const filtered = scores.filter((score) => !score.deleted);
  const withStageFilter = filtered.filter((score) =>
    !stages || !stages[score.stageIdx - 1]?.inactive || !!score.dq
  );

  const grouped = {};
  withStageFilter.forEach((score) => {
    if (!grouped[score.stageIdx]) grouped[score.stageIdx] = {};
    const existing = grouped[score.stageIdx][score.shooterId];
    if (!existing || score.timestamp > existing.timestamp) {
      grouped[score.stageIdx][score.shooterId] = score;
    }
  });

  return range(numStages).map((stageNum) => {
    const stageScores = grouped[stageNum + 1];
    return stageScores ? Object.values(stageScores) : [];
  });
}

export function numShotsPerPaper({ stage }) {
  const targets = stage.targets || {};
  const steelTargets = (targets.poppers || 0)
    + (targets.minipoppers || 0)
    + (targets.plates || 0)
    + (targets['pcc-frangible'] || 0)
    + (targets['pcc-ten-points-poppers'] || 0)
    + (targets['pcc-ten-points-frangible'] || 0);
  const paperTargets = (targets.paper || 0) + (targets.minipaper || 0);
  if (paperTargets === 0) return 0;
  return (stage.rounds - steelTargets) / paperTargets;
}

export function getMultipliers({ scoreMode }) {
  if (scoreMode === 'il-army') {
    return {
      major: { alpha: 5, charlie: 1, delta: 0, miss: -5, noshoot: -5, penalty: -5 },
      minor: { alpha: 5, charlie: 1, delta: 0, miss: -5, noshoot: -5, penalty: -5 },
      invalid: { alpha: 0, charlie: 0, delta: 0, miss: 0, noshoot: 0, penalty: 0 },
    };
  }
  return {
    major: { alpha: 5, charlie: 4, delta: 2, miss: -10, noshoot: -10, penalty: -10 },
    minor: { alpha: 5, charlie: 3, delta: 1, miss: -10, noshoot: -10, penalty: -10 },
    invalid: { alpha: 0, charlie: 0, delta: 0, miss: 0, noshoot: 0, penalty: 0 },
  };
}

export function pad(num, len) {
  const parts = (`${num}`).split('.');
  parts[1] = (parts[1] || '').substr(0, len || 2);
  const frac = padEnd(parts[1] || '', len, '0');
  return `${parts[0]}.${frac}`;
}

export function summarizeScore({ score, stage, mode = 'comstock', powerFactor }) {
  const scoreStr = (score.scorePerTarget || []).join('').split('');
  const targets = stage.targets || {};
  const steelCount = (targets.poppers || 0) + (targets.minipoppers || 0)
    + (targets.plates || 0) + (targets['pcc-frangible'] || 0)
    + (targets['pcc-ten-points-poppers'] || 0) + (targets['pcc-ten-points-frangible'] || 0);

  const ret = {
    time: score.time || 0,
    alphas: scoreStr.filter((s) => s === 'A').length + (score.poppers || 0) + (score.tenPointsPoppers || 0),
    charlies: scoreStr.filter((s) => s === 'C').length,
    deltas: scoreStr.filter((s) => s === 'D').length,
    misses: !score.time ? 0 : scoreStr.filter((s) => s === 'M').length
      + (mode === 'steel-no-miss'
        ? 0
        : steelCount - (score.poppers || 0) - (score.tenPointsPoppers || 0)),
    bonusMisses: scoreStr.filter((s) => s === 'm').length
      + (mode === 'steel-no-miss'
        ? steelCount - (score.poppers || 0) - (score.tenPointsPoppers || 0)
        : 0),
    noShoots: score['no-shoots'] || 0,
    penalties: sum(values(score.penaltiesByType)) || 0,
    bonusField: score.bonusField || 0,
  };

  const multiplier = getMultipliers({ scoreMode: mode })[powerFactor || 'minor'];
  const tenPointsExtra = (score.tenPointsPoppers || 0) * multiplier.alpha;

  ret.total = Math.max(0, (ret.alphas * multiplier.alpha)
    + tenPointsExtra
    + (ret.charlies * multiplier.charlie)
    + (ret.deltas * multiplier.delta)
    + (ret.misses * multiplier.miss)
    + (ret.noShoots * multiplier.noshoot)
    + (ret.penalties * multiplier.penalty))
    + (mode === 'il-army' ? ret.bonusField : 0);

  ret.specialPenaltyPercentageStr = score.specialPenalty ? `${score.specialPenalty}%` : '';
  ret.specialPenaltyValue = -1 * Math.round((-1 * ret.total * (score.specialPenalty || 0)) / 100);
  ret.total -= ret.specialPenaltyValue;

  ret.factor = ret.time === 0 ? 0 : ret.total / ret.time;
  ret.timeStr = ret.time === 0 ? '0' : pad(ret.time, 2);
  ret.factorStr = ret.time === 0 ? '0' : pad(parseInt(Math.round(ret.factor * 10000, 10), 10) / 10000, 4);

  return ret;
}

export function formatTime(seconds) {
  if (!seconds) return '0.00';
  return pad(seconds, 2);
}

export function calculateHitFactor(score, stage) {
  const summary = summarizeScore({ score, stage });
  return summary.factor;
}

export function calculateRawScore(score, stage, scoreMode) {
  const summary = summarizeScore({ score, stage, mode: scoreMode });
  return summary.total;
}

export function calculatePenalties(score) {
  return (score['no-shoots'] || 0) + (sum(values(score.penaltiesByType)) || 0);
}

export function getScoresWithPoints({ scores: $scores, stages, shooters, combined = false, mode }) {
  const dqs = [];

  const scoresPerStageWithShooterInfo = getLatestScoreForShooterPerStage({
    scores: $scores, numStages: stages.length, stages,
  }).map((stageScores, stageIdx) => compact(stageScores.map((s) => {
    const shooter = shooters.find(($shooter) => $shooter.id?.toString() === s.shooterId?.toString())
      || shooters.find(($shooter) => $shooter.id === `${s.matchId}_${s.shooterId}`);
    if (!shooter) return null;
    return {
      ...summarizeScore({
        score: s, stage: stages[stageIdx], powerFactor: shooter['power-factor'], mode,
      }),
      dq: s.dq,
      timestamp: s.timestamp,
      shooterId: shooter.id,
      shooterPublicId: shooter.publicId,
      shooterName: shooter.name,
      shooterTeam: shooter.team,
      shooterIcs: shooter.ics,
      isRo: find(['ro', 'cro', 'so', 'md', 'rm', 'qm'], (role) => ((shooter.labels || []).indexOf(role) > -1)),
      division: shooter.division || 'standard',
      category: shooter.category || 'regular',
      'power-factor': shooter['power-factor'] || 'major',
    };
  })));

  scoresPerStageWithShooterInfo.forEach((stageScores, stage) => stageScores.forEach((score) => {
    if (score.dq) {
      if (!dqs.find((dq) => dq.shooterId === score.shooterId)) {
        dqs.push({ ...score, stage: stage + 1 });
      }
    }
  }));

  scoresPerStageWithShooterInfo.forEach((stageScores, stage) => {
    scoresPerStageWithShooterInfo[stage] = stageScores.filter(
      (score) => !dqs.find((dq) => dq.shooterId === score.shooterId)
    );
  });

  const topFactorPerStage = {};

  scoresPerStageWithShooterInfo.forEach((stageScores, stageIdx) => stageScores.forEach((score) => {
    topFactorPerStage[stageIdx] = topFactorPerStage[stageIdx] || {};
    const division = combined ? 'combined' : score.division;
    topFactorPerStage[stageIdx][division] = topFactorPerStage[stageIdx][division]
      || sortBy(
        stageScores.filter((s) => combined || s.division === score.division),
        (s) => -1 * s.factor
      )[0];

    const topFactor = topFactorPerStage[stageIdx][division];
    const topFactorFactor = Math.round(topFactor.factor * 10000) / 10000;

    const stage = stages[stageIdx];
    const stageTargets = stage?.targets || {};
    const tenPointsExtra = ((stageTargets['pcc-ten-points-poppers'] || 0) + (stageTargets['pcc-ten-points-frangible'] || 0)) * 5;

    score.factor = Math.round(score.factor * 10000) / 10000;
    score.percentage = topFactorFactor ? Math.round((score.factor * 10000) / topFactorFactor) : 0;
    const totalPoints = (stage?.rounds || 0) * 5 + tenPointsExtra + (stage?.bonusField ? 20 : 0);
    score.points = score.time === 0 || topFactor.total === 0 || !topFactorFactor ? 0 : (totalPoints * score.factor) / topFactorFactor;
  }));

  const scoresByStage = scoresPerStageWithShooterInfo.map((stageScores) =>
    sortBy(
      compact(stageScores.map((score) => (score.dq ? null : score))),
      (s) => -1 * s.factor
    )
  );

  const grouped = {};
  flatten(scoresByStage).forEach((s) => {
    if (!grouped[s.shooterId]) {
      grouped[s.shooterId] = {
        ...pick(s, ['shooterId', 'shooterName', 'shooterTeam', 'shooterIcs', 'isRo', 'shooterPublicId', 'division', 'category']),
        score: 0,
      };
    }
    grouped[s.shooterId].score += s.points;
  });

  const scoresOverall = Object.values(grouped);
  scoresOverall.sort((a, b) => b.score - a.score);

  scoresOverall.forEach((score) => {
    const topDivision = combined ? scoresOverall[0] : scoresOverall.find((s) => s.division === score.division);
    score.percentage = topDivision?.score ? Math.round((score.score * 10000) / topDivision.score) : 0;
  });

  scoresPerStageWithShooterInfo.forEach((stageScores) => stageScores.forEach((score) => {
    score.points = Math.round(score.points * 10000) / 10000;
  }));

  return { scoresByStage, scoresOverall, dqs };
}

export function getFlagSrc({ countryCode, size }) {
  if (countryCode === 'ND') {
    return `https://flagcdn.com/${size}x${(size * 3) / 4}/gb-nir.png`;
  }
  return `https://flagcdn.com/${size}x${(size * 3) / 4}/${countryCode.toLowerCase()}.png`;
}

export function renderPublicId(publicId, name, isRo) {
  const parts = (publicId || '').split('|');
  const displayId = parts.length > 1 ? parts[1] : publicId;
  const countryCode = parts.length > 1 ? parts[0] : null;

  return {
    displayId,
    countryCode,
    name,
    isRo,
    flagSrc: countryCode ? getFlagSrc({ countryCode, size: 16 }) : null,
  };
}
